import React, { useEffect, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import truncate from "lodash/truncate"
import cn from "classnames"

import styles from "./AttachmentItem.module.scss"
import { testId } from "../../utility/tests/testId/dialogs"
import { FileType, getFileInfo, selectFileIcon } from "../../utility/common/files"
import { CSS_VAR_COLORS } from "../../helpers/color"
import { XCloseButton } from "../XCloseButton/XCloseButton"
import { faExclamationCircle } from "@fortawesome/pro-light-svg-icons/faExclamationCircle"
import CommonLoader from "../CommonLoader/CommonLoader"
import { useTranslation } from "react-i18next"

export interface AttachmentItemProps {
    title: string
    url: string
    size: number
    onPressCloseButton?: () => void
    onPressItem?: () => void
    containerClassName?: string
    small?: boolean
    file?: File
}

export const UPLOAD_FILE_ERROR = "UPLOAD_FILE_ERROR"
const tNamespace = "dialogs"

const AttachmentItem: React.FC<AttachmentItemProps> = ({
    title,
    url,
    onPressCloseButton,
    onPressItem,
    containerClassName,
    small,
    file
}) => {
    const { itemType, fileExtension } = getFileInfo(url ? url : title)
    const { t } = useTranslation(tNamespace)
    const shortTitle = truncate(title, { length: 16, omission: "..." })
    const urlWithNameParam = url + `?file_name=${title}`

    const [localUrl, setLocalUrl] = useState("") // Для локального превью

    useEffect(() => {
        if (file && itemType === FileType.image) {
            const newLocalUrl = URL.createObjectURL(file)
            setLocalUrl(newLocalUrl)

            return () => URL.revokeObjectURL(newLocalUrl)
        }
    }, [file, itemType])

    const renderInner = () => {
        if (!url) {
            return <CommonLoader />
        }

        if (url === UPLOAD_FILE_ERROR) {
            return (
                <div className={styles.attachmentItem__errorContainer}>
                    <FontAwesomeIcon icon={faExclamationCircle} size="3x" />
                    <div className={styles.attachmentItem__errorInfo}>
                        <span>{t("attachments.not-loaded")}</span>
                    </div>
                </div>
            )
        }

        switch (itemType) {
            case FileType.other:
            case FileType.video:
                return (
                    <FontAwesomeIcon
                        className={styles.attachmentItem__icon}
                        color={CSS_VAR_COLORS.PRIMARY}
                        size="4x"
                        icon={selectFileIcon(itemType, fileExtension)}
                    />
                )
            case FileType.image:
                return <img src={localUrl || url} className={styles.attachmentItem__image} alt={title} />
        }
    }

    return (
        <div className={cn(styles.attachmentItem, containerClassName)} data-testid={testId.attachmentItem}>
            {small ? (
                <div
                    className={styles.attachmentItem__previewContainer}
                    onClick={onPressItem}
                    data-testid={testId.attachmentPreview}
                >
                    {renderInner()}
                </div>
            ) : (
                <div className={styles.attachmentItem__container}>
                    <div className={styles.attachmentItem__headerContainer}>
                        <FontAwesomeIcon
                            color={CSS_VAR_COLORS.PRIMARY}
                            icon={selectFileIcon(itemType, fileExtension)}
                            size="2x"
                        />
                        <div className={styles.attachmentItem__headerTextContainer}>
                            <a
                                className={styles.attachmentItem__headerTextTitle}
                                href={urlWithNameParam}
                                data-testid={testId.attachmentLink}
                                download
                            >
                                {shortTitle}
                            </a>
                        </div>
                    </div>
                    <div
                        className={styles.attachmentItem__previewContainer}
                        onClick={url && url !== UPLOAD_FILE_ERROR ? onPressItem : void 0}
                        data-testid={testId.attachmentPreview}
                    >
                        {renderInner()}
                    </div>
                </div>
            )}
            {Boolean(onPressCloseButton) && (
                <XCloseButton
                    onClick={onPressCloseButton}
                    buttonClassName={styles.attachmentItem__closeButton}
                    iconClassName={styles.attachmentItem__closeIcon}
                    testId={testId.cancelAttachment}
                />
            )}
        </div>
    )
}

export default AttachmentItem
